import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  commercialGovernanceApi,
  type ActivateRequest,
  type CatalogItem,
  type CommercialGovernanceDashboard,
  type CommercialHealth,
  type CreditLedgerEntry,
  type FeatureFlag,
  type ModuleLicense,
  type SubscriptionRecord,
  type UsageMeterRecord,
  type WhiteLabelConfig,
} from '../../api/commercial-governance';

type Tab = 'overview' | 'subscriptions' | 'licenses' | 'features' | 'credits' | 'usage' | 'catalog' | 'white-label';
type Theme = 'light' | 'dark';

function licenseStatusColor(status: string, dark: boolean) {
  if (status === 'purchased' || status === 'enabled' || status === 'enterprise') return dark ? 'text-emerald-400' : 'text-emerald-600';
  if (status === 'trial' || status === 'evaluation') return dark ? 'text-amber-400' : 'text-amber-600';
  if (status === 'disabled' || status === 'expired') return dark ? 'text-red-400' : 'text-red-600';
  return dark ? 'text-slate-400' : 'text-slate-500';
}

export function CommercialGovernancePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<CommercialGovernanceDashboard | null>(null);
  const [health, setHealth] = useState<CommercialHealth | null>(null);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRecord[]>([]);
  const [licenses, setLicenses] = useState<ModuleLicense[]>([]);
  const [features, setFeatures] = useState<FeatureFlag[]>([]);
  const [credits, setCredits] = useState<CreditLedgerEntry[]>([]);
  const [usage, setUsage] = useState<UsageMeterRecord[]>([]);
  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [whiteLabel, setWhiteLabel] = useState<WhiteLabelConfig[]>([]);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, hlth, subs, lics, feats, creds, usg, cat, wl] = await Promise.all([
        commercialGovernanceApi.dashboard(),
        commercialGovernanceApi.health(),
        commercialGovernanceApi.listSubscriptions(),
        commercialGovernanceApi.listLicenses(),
        commercialGovernanceApi.listFeatures(),
        commercialGovernanceApi.listCredits(),
        commercialGovernanceApi.listUsage(),
        commercialGovernanceApi.listCatalog(),
        commercialGovernanceApi.listWhiteLabel(),
      ]);
      setDashboard(dash);
      setHealth(hlth);
      setSubscriptions(Array.isArray(subs) ? subs : []);
      setLicenses(lics);
      setFeatures(feats);
      setCredits(creds);
      setUsage(Array.isArray(usg) ? usg : []);
      setCatalog(cat);
      setWhiteLabel(wl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load commercial platform');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runAction = async (action: 'activate' | 'deactivate', body: ActivateRequest) => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = action === 'activate'
        ? await commercialGovernanceApi.activate(body)
        : await commercialGovernanceApi.deactivate(body);
      setActionMsg(result.message);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : `${action} failed`);
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'subscriptions', label: 'Subscriptions' },
    { id: 'licenses', label: 'Licenses' },
    { id: 'features', label: 'Feature Flags' },
    { id: 'credits', label: 'Credit Center' },
    { id: 'usage', label: 'Usage Analytics' },
    { id: 'catalog', label: 'Commercial Catalog' },
    { id: 'white-label', label: 'White Label' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-500">DEV-000084</p>
          <h1 className="text-2xl font-semibold">Enterprise Commercial Platform</h1>
          <p className={`text-sm ${muted}`}>Subscriptions, module licensing, feature flags, credits, usage metering & revenue governance</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/ai-operations" className="text-emerald-500 hover:underline">AI Platform</Link>
            <span className={muted}>·</span>
            <Link to="/platform/semantic-governance" className="text-emerald-500 hover:underline">Semantic Layer</Link>
            <span className={muted}>·</span>
            <Link to="/billing" className="text-emerald-500 hover:underline">Billing Dashboard</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
          <button type="button" onClick={refresh} disabled={busy} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">Refresh</button>
        </div>
      </div>

      {error && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-red-900 bg-red-950 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{error}</div>}
      {actionMsg && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-emerald-900 bg-emerald-950 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b pb-2" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-emerald-600 text-white' : dark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && health && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Commercial Health Score</p><p className="mt-1 text-3xl font-semibold text-emerald-500">{health.commercial_health_score.toFixed(1)}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>MRR</p><p className="mt-1 text-3xl font-semibold">${health.mrr.toLocaleString()}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>ARR</p><p className="mt-1 text-3xl font-semibold">${health.arr.toLocaleString()}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Active Licenses</p><p className="mt-1 text-3xl font-semibold">{health.active_licenses}</p></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Subscriptions</p><p className="mt-1 text-2xl font-semibold">{dashboard.subscriptions}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Feature Flags</p><p className="mt-1 text-2xl font-semibold">{dashboard.features}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Credit Types</p><p className="mt-1 text-2xl font-semibold">{dashboard.credit_types}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>License Utilization</p><p className="mt-1 text-2xl font-semibold">{health.license_utilization_pct.toFixed(1)}%</p></div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h2 className="font-medium">Extension Points</h2>
            <ul className={`mt-2 space-y-1 text-sm ${muted}`}>
              {dashboard.extension_points.map((ep) => (
                <li key={ep.dev_id}><span className="font-mono text-emerald-500">{ep.dev_id}</span> — {ep.name}: {ep.description}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'subscriptions' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Plan</th><th className="px-4 py-2 text-left">Type</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-right">MRR</th><th className="px-4 py-2 text-left">Modules</th></tr>
            </thead>
            <tbody>
              {subscriptions.map((s) => (
                <tr key={s.id} className="border-t border-slate-800/20">
                  <td className="px-4 py-2 font-medium">{s.plan_name}</td>
                  <td className={`px-4 py-2 ${muted}`}>{s.type}</td>
                  <td className="px-4 py-2">{s.status}</td>
                  <td className="px-4 py-2 text-right">${s.mrr.toLocaleString()}</td>
                  <td className={`px-4 py-2 ${muted}`}>{s.modules?.join(', ') ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'licenses' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => runAction('activate', { module: 'crm' })} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">Activate CRM</button>
            <button type="button" disabled={busy} onClick={() => runAction('deactivate', { module: 'developer' })} className={`rounded-lg border px-3 py-1.5 text-sm ${card} disabled:opacity-50`}>Deactivate Developer</button>
          </div>
          <div className={`overflow-x-auto rounded-xl border ${card}`}>
            <table className="min-w-full text-sm">
              <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
                <tr><th className="px-4 py-2 text-left">Module</th><th className="px-4 py-2 text-left">Display Name</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-left">Plan</th></tr>
              </thead>
              <tbody>
                {licenses.map((l) => (
                  <tr key={l.id} className="border-t border-slate-800/20">
                    <td className="px-4 py-2 font-mono">{l.module}</td>
                    <td className="px-4 py-2">{l.display_name}</td>
                    <td className={`px-4 py-2 ${licenseStatusColor(l.status, dark)}`}>{l.status}</td>
                    <td className={`px-4 py-2 ${muted}`}>{l.plan ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'features' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => runAction('activate', { feature: 'white_label_portal' })} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">Enable White Label</button>
            <button type="button" disabled={busy} onClick={() => runAction('deactivate', { feature: 'planning_ai_copilot' })} className={`rounded-lg border px-3 py-1.5 text-sm ${card} disabled:opacity-50`}>Disable AI Copilot</button>
          </div>
          <div className={`overflow-x-auto rounded-xl border ${card}`}>
            <table className="min-w-full text-sm">
              <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
                <tr><th className="px-4 py-2 text-left">Key</th><th className="px-4 py-2 text-left">Name</th><th className="px-4 py-2 text-left">Scope</th><th className="px-4 py-2 text-left">Enabled</th><th className="px-4 py-2 text-right">Rollout</th></tr>
              </thead>
              <tbody>
                {features.map((f) => (
                  <tr key={f.id} className="border-t border-slate-800/20">
                    <td className="px-4 py-2 font-mono text-xs">{f.key}</td>
                    <td className="px-4 py-2">{f.name}{f.beta && <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-xs text-amber-500">Beta</span>}{f.kill_switch && <span className="ml-2 rounded bg-red-500/20 px-1.5 py-0.5 text-xs text-red-500">Kill</span>}</td>
                    <td className={`px-4 py-2 ${muted}`}>{f.scope}</td>
                    <td className={`px-4 py-2 ${f.enabled ? 'text-emerald-500' : muted}`}>{f.enabled ? 'Yes' : 'No'}</td>
                    <td className={`px-4 py-2 text-right ${muted}`}>{f.rollout_pct ?? 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'credits' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Credit Type</th><th className="px-4 py-2 text-right">Balance</th><th className="px-4 py-2 text-right">Granted</th><th className="px-4 py-2 text-right">Consumed</th><th className="px-4 py-2 text-right">Reserved</th></tr>
            </thead>
            <tbody>
              {credits.map((c) => (
                <tr key={c.id} className="border-t border-slate-800/20">
                  <td className="px-4 py-2 font-mono">{c.credit_type}</td>
                  <td className="px-4 py-2 text-right font-semibold">{c.balance.toLocaleString()}</td>
                  <td className={`px-4 py-2 text-right ${muted}`}>{c.granted.toLocaleString()}</td>
                  <td className={`px-4 py-2 text-right ${muted}`}>{c.consumed.toLocaleString()}</td>
                  <td className={`px-4 py-2 text-right ${muted}`}>{c.reserved.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'usage' && (
        <div className={`overflow-x-auto rounded-xl border ${card}`}>
          <table className="min-w-full text-sm">
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 text-left">Metric</th><th className="px-4 py-2 text-left">Module</th><th className="px-4 py-2 text-left">Period</th><th className="px-4 py-2 text-right">Quantity</th></tr>
            </thead>
            <tbody>
              {usage.map((u) => (
                <tr key={u.id} className="border-t border-slate-800/20">
                  <td className="px-4 py-2 font-mono">{u.metric}</td>
                  <td className={`px-4 py-2 ${muted}`}>{u.module ?? '—'}</td>
                  <td className={`px-4 py-2 ${muted}`}>{u.period}</td>
                  <td className="px-4 py-2 text-right">{u.quantity.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'catalog' && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {catalog.map((item) => (
            <div key={item.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs uppercase text-emerald-500">{item.kind}</p>
                  <h3 className="font-medium">{item.name}</h3>
                </div>
                {item.price_usd != null && item.price_usd > 0 && <p className="font-semibold">${item.price_usd.toLocaleString()}</p>}
              </div>
              <p className={`mt-2 text-sm ${muted}`}>{item.description}</p>
              {item.modules && item.modules.length > 0 && <p className={`mt-2 text-xs ${muted}`}>Modules: {item.modules.join(', ')}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'white-label' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {whiteLabel.map((wl) => (
            <div key={wl.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{wl.partner_name}</h3>
                <span className={`rounded-full px-2 py-0.5 text-xs ${wl.enabled ? 'bg-emerald-500/20 text-emerald-500' : muted}`}>{wl.enabled ? 'Active' : 'Inactive'}</span>
              </div>
              {wl.custom_domain && <p className={`mt-2 text-sm ${muted}`}>{wl.custom_domain}</p>}
              {wl.theme_pack && <p className={`mt-1 text-xs ${muted}`}>Theme: {wl.theme_pack}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
